import { formatCurrency } from "./Utils";

export function getPromoPrice(prix: string | number, taux: number): number {
  const pr = typeof prix === "string" ? parseFloat(prix) : prix;
  if (!Number.isFinite(pr)) return 0;
  return pr * (1 - taux / 100);
}

export function getProductPromoRate(product: any): number {
  if (!product) return 0;
  const cat = product?.categorie?.promotion;
  const sub = product?.sous_categorie?.promotion;
  const prod = product?.promotion;
  if (cat?.is_active) return Number(cat.taux) || 0;
  if (sub?.is_active) return Number(sub.taux) || 0;
  if (prod?.is_active) return Number(prod.taux) || 0;
  return 0;
}

export function hasActivePromotion(product: any): boolean {
  return !!(
    product?.categorie?.promotion?.is_active ||
    product?.sous_categorie?.promotion?.is_active ||
    product?.promotion?.is_active
  );
}

const VAR_SIZE_TYPES = [
  "taille_unique_avec_couleur",
  "taille_variable",
  "taille_variable_prix_unique",
];

export function isVarSizeProduct(type?: string): boolean {
  if (!type) return false;
  return VAR_SIZE_TYPES.includes(type);
}

/** Produit à tailles variables avec prix par variation — choix obligatoire sur la fiche. */
export function requiresSizeSelection(type?: string): boolean {
  return type === "taille_variable";
}

/** Variations en stock avec taille distincte (sélecteur carte produit). */
export function getSizeVariationsForPicker(product: any): any[] {
  const variations = product?.variations ?? [];
  const inStock = variations.filter(
    (v: any) =>
      v.active !== false &&
      Number(v?.quantite) > 0 &&
      v?.taille != null &&
      String(v.taille).trim() !== "",
  );
  return inStock.filter(
    (v: any, index: number, self: any[]) =>
      self.findIndex((x) => x.taille === v.taille) === index,
  );
}

/** Afficher le sélecteur « Choisir la taille » uniquement s'il y a 2+ tailles. */
export function needsSizePickerOnCard(product: any): boolean {
  const type = product?.type;
  const sizeCount = getSizeVariationsForPicker(product).length;
  if (sizeCount <= 1) return false;
  return type === "taille_variable" || type === "taille_variable_prix_unique";
}

/** Première variation en stock utilisable pour le panier (liste / cartes). */
export function getDefaultCartVariation(product: any): any | undefined {
  const sizeOptions = getSizeVariationsForPicker(product);
  if (sizeOptions.length > 0) return sizeOptions[0];

  const variations = product?.variations ?? [];
  if (!variations.length) return undefined;
  const inStock = variations.filter(
    (v: any) => v.active !== false && Number(v?.quantite) > 0,
  );
  return inStock[0] ?? variations[0];
}

export function productHasMultiplePrices(
  product: any,
  promoRate: number,
): boolean {
  if (product?.wholesale_tiers?.length > 0) return true;
  if (!product?.variations?.length) return false;
  if (
    product.variations.some(
      (v: any) => v.wholesale_tiers && v.wholesale_tiers.length > 0,
    )
  ) {
    return true;
  }
  const basePrice = getPromoPrice(parseFloat(String(product.prix)), promoRate);
  const prices = new Set(
    product.variations
      .filter((v: any) => v.active !== false)
      .map((v: any) =>
        v.prix != null && v.prix !== ""
          ? getPromoPrice(parseFloat(String(v.prix)), promoRate)
          : basePrice,
      ),
  );
  return prices.size > 1;
}

export function getVariationPriceRange(
  product: any,
  promoRate: number,
): { min: number; max: number } {
  const basePrice = getPromoPrice(parseFloat(String(product?.prix ?? 0)), promoRate);
  if (!product?.variations?.length) {
    return { min: basePrice, max: basePrice };
  }
  const prices = product.variations
    .filter((v: any) => v.active !== false)
    .map((v: any) =>
      v.prix != null && v.prix !== ""
        ? getPromoPrice(parseFloat(String(v.prix)), promoRate)
        : basePrice,
    )
    .filter((n: number) => Number.isFinite(n));
  if (!prices.length) return { min: basePrice, max: basePrice };
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function getVariationRawPriceRange(
  product: any,
): { min: number; max: number } {
  const base = parseFloat(String(product?.prix ?? 0));
  const safeBase = Number.isFinite(base) ? base : 0;
  if (!product?.variations?.length) {
    return { min: safeBase, max: safeBase };
  }
  const prices = product.variations
    .filter((v: any) => v.active !== false)
    .map((v: any) =>
      v.prix != null && v.prix !== ""
        ? parseFloat(String(v.prix))
        : safeBase,
    )
    .filter((n: number) => Number.isFinite(n));
  if (!prices.length) return { min: safeBase, max: safeBase };
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function formatPriceRange(
  min: number,
  max: number,
  devise?: string,
): string {
  if (min === max) return formatCurrency(min, devise);
  return `${formatCurrency(min, devise)} - ${formatCurrency(max, devise)}`;
}

export function getUnitPriceForVariation(
  product: any,
  variation: any,
  promoRate: number,
): number {
  if (variation?.prix != null && variation?.prix !== "") {
    return getPromoPrice(parseFloat(String(variation.prix)), promoRate);
  }
  return getPromoPrice(parseFloat(String(product?.prix ?? 0)), promoRate);
}

export function isValidProductColor(
  couleur: string | null | undefined,
): boolean {
  if (couleur == null) return false;
  const trimmed = String(couleur).trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return false;
  return (
    /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed) ||
    /^rgba?\(/i.test(trimmed)
  );
}

export function getDisplayableColorVariations(
  variations: any[] | undefined,
): any[] {
  if (!variations?.length) return [];
  return variations
    .filter((v) => Number(v.quantite) > 0)
    .filter(
      (value, index, self) =>
        self.findIndex((v) => v.couleur === value.couleur) === index,
    )
    .filter((v) => isValidProductColor(v.couleur));
}

export function getCardPriceDisplay(
  product: any,
  devise?: string,
): ProductPriceLabels & { showMultipleSizesBadge: boolean; startingFrom: boolean } {
  const promoRate = getProductPromoRate(product);
  const hasPromo = hasActivePromotion(product) && promoRate > 0;
  const multiSize = needsSizePickerOnCard(product);
  const priceRange = getVariationPriceRange(product, promoRate);
  const rawRange = getVariationRawPriceRange(product);

  if (multiSize) {
    const minDisplay = formatCurrency(priceRange.min, devise);
    const minRaw = formatCurrency(rawRange.min, devise);
    return {
      showRange: false,
      startingFrom: true,
      showMultipleSizesBadge: true,
      hasPromo,
      display: `À partir de ${minDisplay}`,
      strikethrough: hasPromo ? `À partir de ${minRaw}` : undefined,
    };
  }

  const labels = getProductPriceLabels(product, devise);
  return {
    ...labels,
    startingFrom: false,
    showMultipleSizesBadge: false,
  };
}

export function parseSizeValue(
  taille?: string,
): { value: number; unit: string } | null {
  if (!taille) return null;
  const match = String(taille)
    .trim()
    .match(/^([\d.,]+)\s*(.+)$/i);
  if (!match) return null;
  const value = parseFloat(match[1].replace(",", "."));
  if (!Number.isFinite(value) || value <= 0) return null;
  return { value, unit: match[2].trim().toLowerCase() };
}

/** Prix unitaire affiché (ex. 12 F/g, 180 F/ml). */
export function formatVariationUnitPrice(
  unitPrice: number,
  taille?: string,
): string | null {
  const parsed = parseSizeValue(taille);
  if (!parsed || unitPrice <= 0) return null;

  let baseValue = parsed.value;
  let unitLabel = parsed.unit;

  if (unitLabel === "kg") {
    baseValue *= 1000;
    unitLabel = "g";
  } else if (unitLabel === "l" || unitLabel === "litre" || unitLabel === "litres") {
    baseValue *= 1000;
    unitLabel = "ml";
  }

  const perUnit = Math.round(unitPrice / baseValue);
  if (!Number.isFinite(perUnit) || perUnit <= 0) return null;
  return `${perUnit} F/${unitLabel}`;
}

export type ProductPriceLabels = {
  showRange: boolean;
  hasPromo: boolean;
  display: string;
  strikethrough?: string;
};

export function getProductPriceLabels(
  product: any,
  devise?: string,
): ProductPriceLabels {
  const promoRate = getProductPromoRate(product);
  const hasPromo = hasActivePromotion(product) && promoRate > 0;
  const isVar = isVarSizeProduct(product?.type);
  const hasMultiple =
    isVar && productHasMultiplePrices(product, promoRate);
  const priceRange = isVar
    ? getVariationPriceRange(product, promoRate)
    : {
        min: getPromoPrice(product?.prix ?? 0, hasPromo ? promoRate : 0),
        max: getPromoPrice(product?.prix ?? 0, hasPromo ? promoRate : 0),
      };
  const rawRange = isVar
    ? getVariationRawPriceRange(product)
    : {
        min: Number(product?.prix) || 0,
        max: Number(product?.prix) || 0,
      };

  const showRange =
    priceRange.min !== priceRange.max &&
    (isVar || hasMultiple);

  if (showRange) {
    return {
      showRange: true,
      hasPromo,
      display: formatPriceRange(priceRange.min, priceRange.max, devise),
      strikethrough: hasPromo
        ? formatPriceRange(rawRange.min, rawRange.max, devise)
        : undefined,
    };
  }

  const baseProductPrice = Number(product?.prix);
  const hasBasePrice = Number.isFinite(baseProductPrice) && baseProductPrice > 0;
  const unitPrice = isVar && !hasBasePrice ? priceRange.min : baseProductPrice;
  const rawUnitPrice = isVar && !hasBasePrice ? rawRange.min : baseProductPrice;

  const display = hasPromo
    ? formatCurrency(getPromoPrice(unitPrice, promoRate), devise)
    : formatCurrency(unitPrice, devise);

  return {
    showRange: false,
    hasPromo,
    display,
    strikethrough: hasPromo
      ? formatCurrency(rawUnitPrice, devise)
      : undefined,
  };
}

export function hasProductStock(product: any): boolean {
  const type = product?.type;
  if (
    type === "taille_variable" ||
    type === "taille_variable_prix_unique" ||
    type === "taille_unique_avec_couleur"
  ) {
    const variations = product?.variations ?? [];
    if (variations.length === 0) {
      return Number(product?.stock) > 0;
    }
    return variations.some((v: any) => Number(v?.quantite) > 0);
  }
  return Number(product?.stock) > 0;
}
